import React, { useEffect, useState, ReactElement } from "react";
import { useDispatch } from "react-redux";

import Button from "components/common/Button";
import { AlertType } from "components/common/Snackbar";

import { copyOnboardingLink, resendOnboardingEmail } from "services/Admin/Customer";
import { showSnackbar } from "store/actions/Utility";

import { MailOutline as EmailIcon, ContentCopy as CopyIcon, DoneOutlined as DoneIcon } from "@mui/icons-material";

import "components/Screens/Customers/CustomerDetials/CustomerProfile/ResendOnboardingEmail/ResendOnboardingEmail.scss";

interface ResendOnboardingEmailProps {
  customerId: string;
}

interface ApiResponse {
  ok: boolean;
  payload?: {
    error?: Array<{
      description: string;
    }>;
    activationLink?: string;
  };
}

function ResendOnboardingEmail({ customerId }: ResendOnboardingEmailProps): ReactElement {
  const [buttonLabel, setButtonLabel] = useState<string>("Copy onboarding link");
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);
  const [isCopyDisabled, setIsCopyDisabled] = useState<boolean>(false);
  const [resendOnboardingEmailCount, setResendOnboardingEmailCount] = useState<number>(0);

  const dispatch = useDispatch();

  useEffect(() => {
    const handleCounter = (): void => {
      if (resendOnboardingEmailCount > 0) {
        setTimeout(() => {
          setResendOnboardingEmailCount(resendOnboardingEmailCount - 1);
        }, 1000);
      } else {
        setIsResendDisabled(false);
      }
    };

    handleCounter();
  }, [resendOnboardingEmailCount]);

  const handleResendOnboardingEmail = async (): Promise<void> => {
    setIsResendDisabled(true);
    const response: ApiResponse = await resendOnboardingEmail(customerId);
    const isSuccess = response?.ok !== false;
    dispatch(
      showSnackbar({
        type: isSuccess ? AlertType.success : AlertType.error,
        message: isSuccess
          ? "Onboarding email sent to customer successfully"
          : response?.payload?.error?.[0]?.description || "Failed to send onboarding email",
        dispatch,
      })
    );

    setResendOnboardingEmailCount(15);
  };

  const getResendOnboardingEmailLabel = (): string => {
    if (resendOnboardingEmailCount > 0) {
      return `Onboarding email sent! (${resendOnboardingEmailCount})`;
    }
    return "Resend Onboarding Email";
  };

  const handleCopyOnboardingLink = async (): Promise<void> => {
    if (isCopyDisabled) return;
    const response: ApiResponse = await copyOnboardingLink(customerId);
    if (response?.ok === false) {
      dispatch(showSnackbar({ type: AlertType.error, message: response?.payload?.error?.[0]?.description, dispatch }));
    } else {
      navigator.clipboard.writeText(response?.payload?.activationLink || "");
      setButtonLabel("Link copied!");
      setIsCopyDisabled(true);
      setTimeout(() => {
        setButtonLabel("Copy onboarding link");
        setIsCopyDisabled(false);
      }, 2 * 1000);
    }
  };

  return (
    <div className="resend-onboarding-email-container">
      <Button
        isFullWidth
        onClick={handleResendOnboardingEmail}
        size="small"
        variant="outlined"
        isDisabled={isResendDisabled}
        label={getResendOnboardingEmailLabel()}
        startIcon={<EmailIcon fontSize="small" />}
      />
      <Button
        isFullWidth
        isDisabled={isCopyDisabled}
        size="small"
        sx={{ marginTop: "20px" }}
        onClick={handleCopyOnboardingLink}
        variant="text"
        label={buttonLabel}
        startIcon={isCopyDisabled ? <DoneIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
      />
    </div>
  );
}

export default ResendOnboardingEmail;
